import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  Pagination,
  Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import axios from 'axios';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import { actions } from '../../actions';
import collections from '../../constants/FirestoreCollections';
import { standardString } from '../../utils/utils';
import SpecialtyItem from './components/SpecialtyItem';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import moment from 'moment';
import { uniq } from 'lodash';
import { handleErrorSwal } from '../../shared/swals';
const MySwal = withReactContent(Swal);
const ITEMS_PER_PAGE = 21;
const urlDialogflow = process.env.REACT_APP_DIALOGFLOW;
const useStyles = (small) => {
  return makeStyles((theme) => ({
    text: {
      marginBottom: theme.spacing(2),
      fontSize: 18,
    },
  }));
};
export default function Specialties() {
  // define styles
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();

  // states
  const [data, setData] = useState();
  const [config, setConfig] = useState();
  const [disabledItems, setDisabledItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(true);

  // hooks
  const firebase = useFirebase();
  const firestore = useFirestore();
  const organization = useSelector((state) => state.app.organization);
  const commonSpecialties = useSelector((state) => state.app.commonSpecialties);
  const appDispatch = useDispatch();
  const profile = useSelector((state) => state.firebase.profile);

  // redux
  const myRef = useRef(null);
  const inputFile = useRef(null);

  useEffect(() => {
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });
    loadConfiguration().finally(() => {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    });
  }, [organization.id, refresh]);

  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [search]);

  const loadConfiguration = async () => {
    if (!organization.id) return;

    try {
      const snapshot = await firestore
        .collection(collections.configurations)
        .where('name', '==', 'commonAgent')
        .where('organizationId', '==', organization.id)
        .get();

      if (snapshot.docs.length > 1) {
        handleErrorSwal();
        return;
      }

      let configData;
      if (snapshot.empty) {
        console.error('loadConfiguration: No se encontró la configuración commonAgent');
        handleErrorSwal();
        return;
      } else {
        configData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }
      setConfig(configData);

      await loadCommonEntities(configData);
    } catch (error) {
      console.error('loadConfiguration:', error);
    }
  };

  const loadCommonEntities = async (configData) => {
    try {
      const token = await firebase.auth().currentUser.getIdToken(true);
      const response = await axios(`${urlDialogflow}/commonEntities`, {
        method: 'get',
        headers: { authorization: `bearer ${token}` },
      });

      if (response?.status === 200 && response?.data?.arr) {
        const { arr: specialtiesResponse } = response.data;
        const _data =
          configData.specialties?.filter((e) => specialtiesResponse.includes(e.value)) ?? [];

        for (const item of specialtiesResponse) {
          if (!_data.some((e) => e.value === item)) {
            _data.push({ value: item, checked: false });
          }
        }
        const associatedPractices =
          configData.specialties?.filter((e) => e.isAssociatedPractice) ?? [];
        for (const item of associatedPractices) {
          const index = _data.findIndex((e) => e.value === item.value && e.isAssociatedPractice);
          if (index !== -1) {
            _data[index] = item;
          } else {
            _data.push(item);
          }
        }
        const sortedData = _data.sort((a, b) => (a.value < b.value ? -1 : 1));
        appDispatch({
          type: actions.commonSpecialties,
          commonSpecialties: sortedData,
        });
        setData(sortedData);
        setDisabledItems(sortedData.filter((e) => e.checked));
      }
    } catch (error) {
      console.error('loadCommonEntities:', error);
    }
  };

  const handleSaveConfiguration = ({ specialty }) => {
    try {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: true, priority: 1 },
      });

      firestore
        .collection(collections.organizations)
        .doc(organization.id)
        .update({
          lastSpecialtiesUpdate: {
            email: profile.email,
            date: moment().format('DD/MM/YYYY HH:mm'),
          },
        });

      let _data = data.map(({ restart, ...e }) => {
        if (!specialty || specialty.value !== e.value)
          return {
            ...e,
            alternativeName: standardString(e.alternativeName),
            value: standardString(e.value),
          };
        else
          return {
            ...e,
            alternativeName: standardString(e.alternativeName),
            value: standardString(e.value),
            restart: true,
          };
      });
      _data = updateSpecialtiesData(_data, commonSpecialties);
      // add to DF agent first
      firebase
        .auth()
        .currentUser.getIdToken(true)
        .then(function (token) {
          axios(`${urlDialogflow}/commonEntities`, {
            method: 'post',
            headers: {
              authorization: `bearer ${token}`,
            },
            data: {
              entities: _data,
              type: 'specialties',
              organizationId: organization.id,
              user: { email: profile?.email },
            },
          })
            .then(({ data }) => {
              _data = _data.map((e) =>
                !e.checked && e.alternativeName ? { ...e, alternativeName: '' } : e,
              );
              setDisabledItems(_data.filter((e) => e.checked));
              firestore
                .collection(collections.configurations)
                .doc(config.id)
                .update({ specialties: _data })
                .then(() => {
                  setData(_data);
                  setRefresh(!refresh);
                });
              if (data && data.length > 0) {
                MySwal.fire({
                  title: '¡Advertencia!',
                  html: `
                <ul>
                ${data.map((error) => `<li>${error}</li>`).join('')}
                </ul>
                <p><strong>El resto de los cambios fueron guardados exitosamente</strong></p>
                `,
                  icon: 'warning',
                });
              } else {
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: 'Actualizado exitosamente!',
                    snackbarSeverity: 'success',
                  },
                });
              }
            })
            .catch(() => {
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: 'Ocurrio un error, intente mas tarde',
                  snackbarSeverity: 'error',
                },
              });
            });
        });
    } catch (error) {
      console.error('handleSaveConfiguration:', error);
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: 'Ocurrio un error, intente mas tarde',
          snackbarSeverity: 'error',
        },
      });
    } finally {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const updateSpecialtiesData = (_data, commonSpecialties) => {
    const commonSpecialtiesMap = new Map();
    commonSpecialties.forEach((specialty) => {
      commonSpecialtiesMap.set(standardString(specialty.value), specialty);
    });

    return _data.map((specialty) => {
      const matchedSpecialty = commonSpecialtiesMap.get(specialty.value);
      const updatedSpecialty = { ...specialty };
      if (updatedSpecialty.checked) {
        let propertyName;
        if (updatedSpecialty.previous && matchedSpecialty.checked) {
          propertyName = getPropertyName(matchedSpecialty);
          if (updatedSpecialty.isSubSpecialty) {
            if (updatedSpecialty.previous.entityType === 'isSubSpecialty') {
              updatedSpecialty.previous.mainSpecialty = matchedSpecialty.mainSpecialty;
              const mainPreviousSpecialty = _data.find(
                (mainSpecialty) => mainSpecialty.value === matchedSpecialty.mainSpecialty,
              );
              updatedSpecialty.previous.mainSpecialtyType = getPropertyName(mainPreviousSpecialty);
            }
          }
        } else {
          propertyName = getPropertyName(updatedSpecialty);
          updatedSpecialty.previous = {};
          if (updatedSpecialty.isSubSpecialty) {
            updatedSpecialty.previous.mainSpecialty = updatedSpecialty.mainSpecialty;
            const mainSpecialty = _data.find(
              (mainSpecialty) => mainSpecialty.value === updatedSpecialty.mainSpecialty,
            );
            updatedSpecialty.previous.mainSpecialtyType = getPropertyName(mainSpecialty);
          }
        }
        updatedSpecialty.previous.entityType = propertyName;
      } else {
        if (matchedSpecialty.isSubSpecialty) {
          updatedSpecialty.previous.mainSpecialty = matchedSpecialty.mainSpecialty;
          const mainSpecialty = _data.find(
            (mainSpecialty) => mainSpecialty.value === matchedSpecialty.mainSpecialty,
          );
          if (mainSpecialty.checked) {
            updatedSpecialty.previous.mainSpecialtyType = getPropertyName(mainSpecialty);
          } else {
            const mainSpecialty = commonSpecialtiesMap.get(matchedSpecialty.mainSpecialty);
            updatedSpecialty.previous.mainSpecialtyType = getPropertyName(mainSpecialty);
          }
        } else if (
          matchedSpecialty.previous &&
          matchedSpecialty.previous.entityType === 'isSubSpecialty'
        ) {
          const mainSpecialty = _data.find(
            (mainSpecialty) => mainSpecialty.value === matchedSpecialty.previous.mainSpecialty,
          );
          if (mainSpecialty && !mainSpecialty.checked) delete updatedSpecialty.previous;
        }
      }
      return updatedSpecialty;
    });
  };

  const handleChange = useCallback((item) => {
    setData((prevItems) => prevItems.map((_item) => (_item.value !== item.value ? _item : item)));
  }, []);

  const copySpecialties = () => {
    const tmpData = data.map((e) => e.value).join('\n');
    navigator.clipboard.writeText(tmpData);
    appDispatch({
      type: actions.alert,
      alert: {
        snackbarOpen: true,
        snackbarMessage: `Listado copiado en portapapeles`,
        snackbarSeverity: 'info',
      },
    });
  };

  const exportConfiguration = () => {
    const text = JSON.stringify(config.specialties);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      // eslint-disable-next-line no-undef
      new Blob([text], { type: `text/plain` }),
    );
    a.download = `${organization.id}-${moment().format('YYYY-MM-DD')}-specialties.json`;
    a.click();
  };

  const listMissingSpecialtiesWithCopy = async (entities = []) => {
    try {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: true, priority: 1 },
      });
      const token = await firebase.auth().currentUser.getIdToken(true);
      const response = await axios(`${urlDialogflow}/missingAPIEntities`, {
        method: 'get',
        headers: { authorization: `bearer ${token}` },
        params: {
          organizationId: organization.id,
          type: 'specialties',
        },
      });
      if (response.status !== 200) {
        handleErrorSwal();
        return;
      }
      const missingSpecialties = response.data;

      MySwal.fire({
        title: '¡Advertencia!',
        html: `
        <p><strong>Estas especialidades figuran en la API pero no han sido configuradas:</strong></p>
        <div style="display: flex; flex-wrap: wrap; gap: 15px; justify-content: flex-start; overflow-y: auto; max-height: 350px;">
          <ul id="specialtiesList" style="
            list-style-type: none;
            padding: 0;
            margin: 0;
            column-count: ${missingSpecialties.length > 10 ? 2 : 1};
            column-gap: 30px;
            width: 100%;
          ">
            ${missingSpecialties
              .map(
                (specialty) => `<li style="margin-bottom: 8px; font-size: 14px;">${specialty}</li>`,
              )
              .join('')}
          </ul>
        </div>
        <button id="copyButton" style="
          background-color: #2778c4;
          color: white;
          padding: 0.625em 1.1em;
          font-size: 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 46px;
        ">Copiar lista</button>
      `,
        showConfirmButton: false,
        icon: 'warning',
        customClass: {
          popup: 'swal-wide',
        },
        width: '600px',
        showCloseButton: true,
        scrollbarPadding: false,
        willOpen: () => {
          const copyButton = document.getElementById('copyButton');
          const specialtiesList = document.getElementById('specialtiesList');

          copyButton.addEventListener('click', () => {
            const specialtiesText = [...specialtiesList.querySelectorAll('li')]
              .map((li) => li.textContent)
              .join('\n');

            navigator.clipboard
              .writeText(specialtiesText)
              .then(() => {
                MySwal.fire({
                  title: '¡Copiado!',
                  text: 'La lista de especialidades ha sido copiada al portapapeles.',
                  icon: 'success',
                });
              })
              .catch(() => {
                handleErrorSwal('No se pudo copiar la lista. Inténtalo nuevamente.');
              });
          });
        },
      });
    } catch (error) {
      console.error('listMissingSpecialtiesWithCopy', error.response.data);
      handleErrorSwal();
    } finally {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const handleChangeFile = (e) => {
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });
    // eslint-disable-next-line no-undef
    const reader = new FileReader();
    const file = e.target.files[0];

    reader.onloadend = () => {
      try {
        // eslint-disable-next-line no-unused-vars
        const specialties = JSON.parse(reader.result);
        if (specialties) {
          firestore
            .collection(collections.configurations)
            .doc(config.id)
            .update({
              specialties,
            })
            .then(() => {
              setConfig({ ...config, specialties });
              appDispatch({
                type: actions.changeLoading,
                loading: { loading: false, priority: 1 },
              });
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: 'Actualizado correctamente',
                  snackbarSeverity: 'success',
                },
              });
            })
            .catch((error) => {
              throw error;
            });
        }
      } catch (err) {
        appDispatch({
          type: actions.changeLoading,
          loading: { loading: false, priority: 1 },
        });
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: 'Ocurrio un error',
            snackbarSeverity: 'error',
          },
        });
      }
    };
    reader.readAsText(file);
  };

  const hasPracticeService =
    organization && organization.services && organization.services.practices;

  const filteredData =
    data &&
    [
      ...data
        .filter((e) => standardString(e.value).indexOf(standardString(search)) === 0)
        .sort((a, b) => (a.value < b.value ? -1 : 1)),
    ]
      .filter(
        (e) =>
          standardString(e.value).indexOf(standardString(search)) !== -1 ||
          standardString(e.mainSpecialty).indexOf(standardString(search)) !== -1,
      )
      .filter(
        (e) =>
          !filter ||
          filter === 'all' ||
          (filter === 'selected' && e.checked) ||
          (filter === 'specialties' &&
            e.checked &&
            !e.noSpecialty &&
            !e.composedSpecialty &&
            !e.practices) ||
          (filter === 'noSpecialties' && e.noSpecialty) ||
          (filter === 'composedSpecialties' && e.composedSpecialty) ||
          (filter === 'practices' && e.practices),
      );

  const getPropertyName = (specialty) => {
    if (!specialty) return null;
    return specialty.noSpecialty
      ? 'noSpecialty'
      : specialty.composedSpecialty
      ? 'composedSpecialty'
      : specialty.isSubSpecialty
      ? 'isSubSpecialty'
      : specialty.practices
      ? 'practices'
      : 'specialty';
  };

  const calculatePageCount = (length) => Math.ceil(length / ITEMS_PER_PAGE);
  return (
    <Box ref={myRef}>
      <Typography className={classes.text}>
        Esta sección permite configurar qué especialidades se van a agregar al agente de Dialogflow
        del cliente.
      </Typography>
      <Typography sx={{ mb: 3, fontSize: 18 }}>
        ℹ️ Para que se pueda guardar la configuración, se deben tildar al menos 10 especialidades.
      </Typography>
      <Box display="flex">
        <FormControl sx={{ minWidth: '300px' }} className={classes.formControl}>
          <TextField
            id="standard-basic"
            label="Buscar"
            onChange={(event) => {
              if (page !== 1) setPage(1);
              setSearch(event.target.value);
            }}
          />
        </FormControl>
        <Box ml={2}>
          <FormControl sx={{ minWidth: '300px' }}>
            <InputLabel id="demo-simple-select-label">Filtro</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={filter}
              label="Día inicio"
              onChange={(e) => {
                setFilter(e.target.value);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="selected">Seleccionados</MenuItem>
              <MenuItem value="specialties">Se ofrecen</MenuItem>
              <MenuItem value="noSpecialties">No se ofrecen</MenuItem>
              <MenuItem value="composedSpecialties">Especialidades compuestas</MenuItem>
              <MenuItem value="practices">Practicas</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {data?.length > ITEMS_PER_PAGE ? (
        <Box width="100%" py={4} display="flex" justifyContent="center">
          <Pagination
            siblingCount={1}
            count={
              !search && (!filter || filter === 'all')
                ? calculatePageCount(data.length)
                : calculatePageCount(filteredData.length)
            }
            color="primary"
            page={page}
            onChange={(e, value) => {
              setPage(value);
              myRef.current.scrollIntoView();
            }}
          />
        </Box>
      ) : null}
      {data && data.length === 0 && <Typography>No hay especialidades aún</Typography>}
      {data && data.length > 0 && (
        <Box my={4} display="flex" alignItems="center">
          <Button
            className={classes.buttonSave}
            variant="contained"
            color="primary"
            onClick={handleSaveConfiguration}
            disabled={data.filter((e) => e.checked).length < 5}
          >
            Guardar configuración
          </Button>
          <Button sx={{ ml: 2 }} variant="outlined" color="primary" onClick={copySpecialties}>
            Copiar listado
          </Button>
          <Button sx={{ ml: 2 }} variant="outlined" color="primary" onClick={exportConfiguration}>
            Exportar configuracion
          </Button>
          <Grid id="popup-btn" item align="left" sx={{ position: 'relative', height: '100%' }}>
            <Button
              sx={{ ml: 2 }}
              variant="outlined"
              color="primary"
              onClick={() => inputFile.current.click()}
            >
              Importar configuracion
            </Button>
            <input
              onChange={handleChangeFile}
              type="file"
              ref={inputFile}
              style={{ display: 'none' }}
            />
          </Grid>
          <Button
            sx={{ ml: 2 }}
            variant="outlined"
            color="secondary"
            onClick={async () =>
              listMissingSpecialtiesWithCopy(['RayosX', 'SpecialtyCirugiaGeneral'])
            }
          >
            Verificar especialidades
          </Button>
        </Box>
      )}
      <Grid container spacing={2}>
        {filteredData
          ?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
          .map((specialty) => (
            <Grid item xs={12} sm={6} md={4} key={specialty.value}>
              <SpecialtyItem
                key={specialty.value}
                specialty={specialty}
                subSpecialties={uniq(
                  data.filter((e) => !!(e.mainSpecialty === specialty.value)).map((e) => e.value),
                )}
                handleChange={handleChange}
                disabledItems={disabledItems}
                hasPracticeService={hasPracticeService}
                disabledRestart={data.filter((e) => e.checked).length < 10}
                handleRestart={() => handleSaveConfiguration({ specialty })}
              />
            </Grid>
          ))}
      </Grid>
      {data?.length > ITEMS_PER_PAGE ? (
        <Box width="100%" py={4} display="flex" justifyContent="center">
          <Pagination
            siblingCount={1}
            count={
              !search && (!filter || filter === 'all')
                ? calculatePageCount(data.length)
                : calculatePageCount(filteredData.length)
            }
            color="primary"
            page={page}
            onChange={(e, value) => {
              setPage(value);
              myRef.current.scrollIntoView();
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
